import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { BotaoContorno } from "../components/ui/BotaoAcao";
import { BotaoTopoPrimario } from "../components/ui/BotoesTopo";
import { CampoTexto } from "../components/ui/CampoTexto";
import { CampoTelefoneDdi } from "../components/ui/CampoTelefoneDdi";
import { DDI_PADRAO, normalizarDdi } from "../utils/paisesTelefone";
import { ChipSelecao } from "../components/ui/ChipSelecao";
import { ModalCadastro } from "../components/ui/ModalCadastro";
import { ModalConfirmacao } from "../components/ui/ModalConfirmacao";
import { UploadImagem } from "../components/ui/UploadImagem";
import { MedidorForcaSenha, senhaAtendeRequisitos } from "../components/ui/MedidorForcaSenha";
import { Configurar2FAModal } from "./Configurar2FAModal";
import {
  alterarSenhaUsuario,
  desativarDoisFatores,
  obterDadosPerfilUsuario,
  removerAvatarUsuario,
  salvarAvatarUsuario,
  salvarPerfilUsuario,
} from "../api/usuario";
import { urlAsset } from "../utils/assets";
import { useIdioma } from "../i18n/IdiomaProvider";
import { mostrarToast, mostrarToastErro } from "../utils/toast";
import styles from "./AvatarPerfilModal.module.css";

interface AvatarPerfilModalProps {
  aberto: boolean;
  onFechar: () => void;
  fallbackNome: string;
  avatarSessao?: string;
  onAtualizado?: () => Promise<void> | void;
  forcarTrocaSenha?: boolean;
  forcar2FA?: boolean;
  onSenhaAlterada?: () => Promise<void> | void;
}

/* Duas abas: o cartão de dois fatores foi para dentro de "Senha de acesso", que
   é onde o usuário já está mexendo em credenciais. */
type AbaPerfil = "dados" | "senha";

const IcoUsuario = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21a8 8 0 0 0-16 0" />
    <circle cx="12" cy="8" r="4" />
  </svg>
);

const IcoCadeado = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

function erroForcaSenha(senha: string): string | null {
  if (senha.length < 8) return "A senha deve ter ao menos 8 caracteres.";
  if (!/[A-Z]/.test(senha)) return "A senha deve conter ao menos uma letra maiúscula.";
  if (!/[0-9]/.test(senha)) return "A senha deve conter ao menos um número.";
  if (!/[^a-zA-Z0-9]/.test(senha)) return "A senha deve conter ao menos um caractere especial.";
  return null;
}

const IcoEscudo = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3 4 6v6c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V6l-8-3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export function AvatarPerfilModal({
  aberto,
  onFechar,
  fallbackNome,
  avatarSessao,
  onAtualizado,
  forcarTrocaSenha = false,
  forcar2FA = false,
  onSenhaAlterada,
}: AvatarPerfilModalProps) {
  const { traduzir } = useIdioma();
  const [abaAtiva, setAbaAtiva] = useState<AbaPerfil>("dados");
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [processandoAvatar, setProcessandoAvatar] = useState(false);
  const [nome, setNome] = useState(fallbackNome);
  const [telefone, setTelefone] = useState("");
  const [telefoneDdi, setTelefoneDdi] = useState(DDI_PADRAO);
  const [avatarUrl, setAvatarUrl] = useState(avatarSessao ? urlAsset(avatarSessao) : "");
  const [senhaAtual, setSenhaAtual] = useState("");
  const [senhaNova, setSenhaNova] = useState("");
  const [senhaNovaConfirmacao, setSenhaNovaConfirmacao] = useState("");
  const [senhaAtualVisivel, setSenhaAtualVisivel] = useState(false);
  const [senhaNovaVisivel, setSenhaNovaVisivel] = useState(false);
  const [senhaConfirmacaoVisivel, setSenhaConfirmacaoVisivel] = useState(false);
  const [alterandoSenha, setAlterandoSenha] = useState(false);
  const [doisFatoresAtivo, setDoisFatoresAtivo] = useState(false);
  const [modalConfigurar2FA, setModalConfigurar2FA] = useState(false);
  const [confirmandoDesativacao, setConfirmandoDesativacao] = useState(false);
  const [senhaDesativar, setSenhaDesativar] = useState("");
  const [desativando, setDesativando] = useState(false);

  async function carregarPerfil() {
    setCarregando(true);

    try {
      const perfil = await obterDadosPerfilUsuario();
      setNome(perfil?.nomePerfil?.trim() || fallbackNome);
      setTelefone(perfil?.telefonePerfil || "");
      setTelefoneDdi(normalizarDdi(perfil?.telefonePerfilDdi));
      setAvatarUrl(perfil?.urlImagem || urlAsset(perfil?.idImagem || avatarSessao || ""));
      setDoisFatoresAtivo(!!perfil?.doisFatoresAtivo);
    } catch (loadError) {
      mostrarToastErro(traduzir(loadError instanceof Error ? loadError.message : "Erro ao carregar o perfil."));
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    if (!aberto) return;
    setAbaAtiva(forcarTrocaSenha || forcar2FA ? "senha" : "dados");
    setSenhaAtual("");
    setSenhaNova("");
    setSenhaNovaConfirmacao("");
    setSenhaAtualVisivel(false);
    setSenhaNovaVisivel(false);
    setSenhaConfirmacaoVisivel(false);
    setSenhaDesativar("");
    void carregarPerfil();
  }, [aberto, avatarSessao, fallbackNome, forcarTrocaSenha, forcar2FA]);

  async function sincronizarAtualizacao() {
    await onAtualizado?.();
    await carregarPerfil();
  }

  async function handleSalvar() {
    setSalvando(true);

    try {
      const resposta = await salvarPerfilUsuario(nome.trim(), telefone, telefoneDdi);
      if (resposta?.sucesso === false) {
        throw new Error(resposta.erro || "Não foi possível salvar o perfil.");
      }
      await sincronizarAtualizacao();
      mostrarToast(traduzir("Dados do seu perfil atualizados com sucesso."), "sucesso");
      onFechar();
    } catch (saveError) {
      mostrarToastErro(traduzir(saveError instanceof Error ? saveError.message : "Erro ao salvar o perfil."));
    } finally {
      setSalvando(false);
    }
  }

  async function handleRemoverAvatar() {
    setProcessandoAvatar(true);

    try {
      const resposta = await removerAvatarUsuario();
      if (resposta?.sucesso === false) {
        throw new Error(resposta.erro || "Não foi possível remover a foto.");
      }
      setAvatarUrl("");
      await sincronizarAtualizacao();
      mostrarToast(traduzir("Foto do seu perfil removida com sucesso."), "sucesso");
    } catch (removeError) {
      mostrarToastErro(traduzir(removeError instanceof Error ? removeError.message : "Erro ao remover a foto."));
    } finally {
      setProcessandoAvatar(false);
    }
  }

  async function handleSelecionarImagem(base64: string) {
    setProcessandoAvatar(true);

    try {
      const resposta = await salvarAvatarUsuario(base64);
      if (resposta?.sucesso === false) {
        throw new Error(resposta.erro || "Não foi possível salvar a foto.");
      }
      setAvatarUrl(resposta?.url || "");
      await sincronizarAtualizacao();
      mostrarToast(traduzir("Foto de perfil salva com sucesso."), "sucesso");
    } catch (uploadError) {
      mostrarToastErro(traduzir(uploadError instanceof Error ? uploadError.message : "Erro ao processar a foto."));
    } finally {
      setProcessandoAvatar(false);
    }
  }

  async function handleAlterarSenha() {
    if (!senhaAtual || !senhaNova || !senhaNovaConfirmacao) {
      mostrarToastErro(traduzir("Informe a senha atual, a nova senha e a confirmação."));
      return;
    }
    if (senhaNova !== senhaNovaConfirmacao) {
      mostrarToastErro(traduzir("A confirmação não corresponde à nova senha."));
      return;
    }
    const erroSenha = erroForcaSenha(senhaNova);
    if (erroSenha) {
      mostrarToastErro(traduzir(erroSenha));
      return;
    }

    setAlterandoSenha(true);

    try {
      const resposta = await alterarSenhaUsuario(senhaAtual, senhaNova);
      if (resposta?.sucesso === false) {
        throw new Error(resposta.erro || "Não foi possível alterar a senha.");
      }
      setSenhaAtual("");
      setSenhaNova("");
      setSenhaNovaConfirmacao("");
      mostrarToast(traduzir("Senha alterada com sucesso."), "sucesso");
      await onAtualizado?.();
      await onSenhaAlterada?.();
    } catch (senhaError) {
      mostrarToastErro(traduzir(senhaError instanceof Error ? senhaError.message : "Erro ao alterar a senha."));
    } finally {
      setAlterandoSenha(false);
    }
  }

  async function handleDesativar2FA() {
    if (!senhaDesativar) {
      mostrarToastErro(traduzir("Informe sua senha para desativar."));
      return;
    }
    setDesativando(true);
    try {
      const resposta = await desativarDoisFatores(senhaDesativar);
      if (resposta?.sucesso === false) {
        throw new Error(resposta.erro || "Não foi possível desativar a autenticação de dois fatores.");
      }
      setDoisFatoresAtivo(false);
      setConfirmandoDesativacao(false);
      setSenhaDesativar("");
      mostrarToast(traduzir("Autenticação de dois fatores desativada."), "sucesso");
    } catch (desativarError) {
      mostrarToastErro(traduzir(desativarError instanceof Error ? desativarError.message : "Erro ao desativar a autenticação de dois fatores."));
    } finally {
      setDesativando(false);
    }
  }

  return (
    <>
    <ModalCadastro
      aberto={aberto}
      onFechar={onFechar}
      titulo={traduzir(forcarTrocaSenha ? "Crie sua nova senha" : forcar2FA ? "Proteja sua conta" : "Editar perfil")}
      subtitulo={traduzir(forcarTrocaSenha ? "Por segurança, troque a senha temporária para continuar." : forcar2FA ? "Ative a autenticação de dois fatores para continuar." : "Atualize os dados, a imagem e a senha do usuário.")}
      largura={720}
      rodape={
        <div className={styles.footer}>
          <BotaoContorno estilo="topo" onClick={onFechar} disabled={salvando || alterandoSenha || processandoAvatar}>
            {traduzir("Cancelar")}
          </BotaoContorno>
          {abaAtiva === "dados" && (
            <BotaoTopoPrimario onClick={() => void handleSalvar()} carregando={salvando}>
              {traduzir("Salvar")}
            </BotaoTopoPrimario>
          )}
          {abaAtiva === "senha" && (
            <BotaoTopoPrimario
              onClick={() => void handleAlterarSenha()}
              carregando={alterandoSenha}
              disabled={
                !senhaAtual
                || !senhaAtendeRequisitos(senhaNova)
                || senhaNova !== senhaNovaConfirmacao
              }
            >
              {traduzir("Alterar senha")}
            </BotaoTopoPrimario>
          )}
        </div>
      }
    >
      <div className={styles.modalBody}>
        <nav className={styles.abas} aria-label={traduzir("Seções do perfil")}>
          {!forcarTrocaSenha && !forcar2FA && (
            <ChipSelecao selecionado={abaAtiva === "dados"} tamanho="padrao" mostrarIndicadorSelecao onClick={() => setAbaAtiva("dados")}>
              Dados do usuário
            </ChipSelecao>
          )}
          <ChipSelecao selecionado={abaAtiva === "senha"} tamanho="padrao" mostrarIndicadorSelecao onClick={() => setAbaAtiva("senha")}>
            Senha de acesso e segurança
          </ChipSelecao>
        </nav>

        {abaAtiva === "dados" && (
          <>
            <div className={styles.fields}>
              <CampoTexto
                rotulo={traduzir("Nome")}
                value={nome}
                onChange={(event) => setNome(event.target.value)}
                placeholder={traduzir("Seu nome")}
                iconeInicio={<IcoUsuario />}
                disabled={carregando || salvando}
              />
              <CampoTelefoneDdi
                rotulo={traduzir("Telefone")}
                valor={telefone}
                ddi={telefoneDdi}
                onChange={setTelefone}
                onChangeDdi={setTelefoneDdi}
                disabled={carregando || salvando}
              />
            </div>

            <section className={styles.secaoImagem}>
              <h3>{traduzir("Imagem do perfil")}</h3>
              <UploadImagem
                url={avatarUrl}
                nome={nome || fallbackNome}
                desabilitado={processandoAvatar || carregando}
                carregando={processandoAvatar}
                ajusteCircular
                onSelecionar={(dataUrl) => void handleSelecionarImagem(dataUrl)}
                onRemover={() => handleRemoverAvatar()}
                onVisualizar={() => avatarUrl && window.open(avatarUrl, "_blank", "noopener,noreferrer")}
                tituloVisualizar={traduzir("Visualizar imagem do perfil")}
              />
            </section>
          </>
        )}

        {abaAtiva === "senha" && (
          <div className={styles.fields}>
            <CampoTexto
              rotulo={traduzir("Senha atual")}
              type={senhaAtualVisivel ? "text" : "password"}
              value={senhaAtual}
              onChange={(event) => setSenhaAtual(event.target.value)}
              placeholder={traduzir("Digite a senha atual")}
              iconeInicio={<IcoCadeado />}
              acaoFim={
                <button
                  type="button"
                  className={`${styles.botaoVisualizarSenha} tooltip`}
                  data-tooltip={traduzir(senhaAtualVisivel ? "Ocultar senha" : "Mostrar senha")}
                  aria-label={traduzir(senhaAtualVisivel ? "Ocultar senha" : "Mostrar senha")}
                  onClick={() => setSenhaAtualVisivel((visivel) => !visivel)}
                >
                  {senhaAtualVisivel ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
              disabled={alterandoSenha}
            />
            <CampoTexto
              rotulo={traduzir("Nova senha")}
              type={senhaNovaVisivel ? "text" : "password"}
              value={senhaNova}
              onChange={(event) => setSenhaNova(event.target.value)}
              placeholder={traduzir("Digite a nova senha")}
              iconeInicio={<IcoCadeado />}
              acaoFim={
                <button
                  type="button"
                  className={`${styles.botaoVisualizarSenha} tooltip`}
                  data-tooltip={traduzir(senhaNovaVisivel ? "Ocultar senha" : "Mostrar senha")}
                  aria-label={traduzir(senhaNovaVisivel ? "Ocultar senha" : "Mostrar senha")}
                  onClick={() => setSenhaNovaVisivel((visivel) => !visivel)}
                >
                  {senhaNovaVisivel ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
              disabled={alterandoSenha}
            />
            <CampoTexto
              rotulo={traduzir("Confirmar nova senha")}
              type={senhaConfirmacaoVisivel ? "text" : "password"}
              value={senhaNovaConfirmacao}
              onChange={(event) => setSenhaNovaConfirmacao(event.target.value)}
              placeholder={traduzir("Digite a nova senha novamente")}
              iconeInicio={<IcoCadeado />}
              acaoFim={
                <button
                  type="button"
                  className={`${styles.botaoVisualizarSenha} tooltip`}
                  data-tooltip={traduzir(senhaConfirmacaoVisivel ? "Ocultar senha" : "Mostrar senha")}
                  aria-label={traduzir(senhaConfirmacaoVisivel ? "Ocultar senha" : "Mostrar senha")}
                  onClick={() => setSenhaConfirmacaoVisivel((visivel) => !visivel)}
                >
                  {senhaConfirmacaoVisivel ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
              disabled={alterandoSenha}
            />
            {/* Requisitos sempre à vista: o usuário sabe o que a senha precisa
                atender antes de começar a digitar, não depois. */}
            <MedidorForcaSenha senha={senhaNova} />
            {senhaNovaConfirmacao && senhaNova !== senhaNovaConfirmacao && (
              <p className={styles.dicaSenha}>
                {traduzir("A confirmação não corresponde à nova senha.")}
              </p>
            )}

            <div className={styles.cardSeguranca}>
              <span className={styles.iconeSeguranca}><IcoEscudo /></span>
              <div className={styles.textoSeguranca}>
                <strong>{traduzir("Autenticação de dois fatores")}</strong>
                <span>
                  {traduzir(
                    doisFatoresAtivo
                      ? "Sua conta está protegida: a cada login será pedido também o código do seu aplicativo autenticador."
                      : "Mesmo que alguém consiga sua senha, não conseguirá acessar sua conta sem o código do seu celular."
                  )}
                </span>
              </div>
              {doisFatoresAtivo && !forcar2FA ? (
                <BotaoContorno estilo="topo" onClick={() => setConfirmandoDesativacao(true)}>
                  {traduzir("Desabilitar 2FA")}
                </BotaoContorno>
              ) : (
                <BotaoTopoPrimario onClick={() => setModalConfigurar2FA(true)}>
                  {traduzir("Habilitar 2FA")}
                </BotaoTopoPrimario>
              )}
            </div>
          </div>
        )}
      </div>
    </ModalCadastro>

      <Configurar2FAModal
        aberto={modalConfigurar2FA}
        onFechar={() => setModalConfigurar2FA(false)}
        onHabilitado={() => {
          setDoisFatoresAtivo(true);
          void onAtualizado?.();
        }}
      />

      <ModalConfirmacao
        aberto={confirmandoDesativacao}
        titulo={traduzir("Desabilitar autenticação de dois fatores")}
        mensagem={
          <div className={styles.confirmacaoSenha}>
            <p>{traduzir("Confirme sua senha atual para desabilitar a autenticação de dois fatores.")}</p>
            <CampoTexto
              type="password"
              value={senhaDesativar}
              onChange={(event) => setSenhaDesativar(event.target.value)}
              placeholder={traduzir("Digite a senha atual")}
              iconeInicio={<IcoCadeado />}
              disabled={desativando}
            />
          </div>
        }
        textoConfirmar={traduzir("Desabilitar")}
        perigo
        processando={desativando}
        onConfirmar={() => void handleDesativar2FA()}
        onCancelar={() => {
          if (desativando) return;
          setConfirmandoDesativacao(false);
          setSenhaDesativar("");
        }}
      />
    </>
  );
}
